// AddDrawerContext.js
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommonDrawer from '../commonComponent/drawerComponent/CommonDrawer';
import DrawerHeader from '../commonComponent/drawerComponent/DrawerHeader';

// Create a context for managing the state of the Add Drawer
const AddDrawerContext = createContext(null);

// Global event name for closing all drawers
const CLOSE_ALL_DRAWERS_EVENT = 'closeAllDrawers';

// Custom hook to access the Add Drawer context
export function useDrawer() {
  const context = useContext(AddDrawerContext);
  if (context === null || context === undefined) {
    throw new Error('useDrawer must be used within an AddDrawerProvider. Make sure the component is wrapped with AddDrawerProvider.');
  }
  return context;
}

// Helper function to close all drawer instances globally
export function closeAllDrawers() {
  window.dispatchEvent(new CustomEvent(CLOSE_ALL_DRAWERS_EVENT));
}

/**
 * AddDrawerProvider - wraps children components and provides drawer control context
 * @param {ReactNode} children - The children that will use the context
 * @param {ReactNode} component - The content to display inside the drawer
 */
export const AddDrawerProvider = ({ children, drawerSx = {} }) => {
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);
  const clearContentTimeoutRef = useRef(null);

  // Local state to control drawer open/close
  const [isOrgDrawerOpen, setOrgDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState(null);
  const [title, setTitle] = useState('');
  const [tooltip, setTooltip] = useState('');
  const [onCloseCallback, setOnCloseCallback] = useState(null);
  const [hideDrawerHeader, setHideDrawerHeader] = useState(false);

  // Enhanced setOrgDrawerOpen that triggers callback when closing
  const handleSetOrgDrawerOpen = (open) => {
    if (!open && onCloseCallback) {
      // Drawer is closing, trigger callback
      if (typeof onCloseCallback === 'function') {
        onCloseCallback();
      }
      // Clear the callback after invoking it to prevent stale callbacks
      setOnCloseCallback(null);
    }
    setOrgDrawerOpen(open);
  };

  // Custom close function that clears content when closing
  const handleCloseDrawer = () => {
    handleSetOrgDrawerOpen(false);
    // Clear any existing timeout
    if (clearContentTimeoutRef.current) {
      clearTimeout(clearContentTimeoutRef.current);
    }
    // Clear drawer content after a short delay to allow drawer animation to complete
    clearContentTimeoutRef.current = setTimeout(() => {
      setDrawerContent(null);
      setTitle('');
      setTooltip('');
      setHideDrawerHeader(false);
      clearContentTimeoutRef.current = null;
    }, 300);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clearContentTimeoutRef.current) {
        clearTimeout(clearContentTimeoutRef.current);
      }
    };
  }, []);

  // Close drawer when route changes (e.g., when clicking NavItem)
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      if (isOrgDrawerOpen) {
        handleCloseDrawer(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isOrgDrawerOpen]);

  // Listen for global close all drawers event
  useEffect(() => {
    const handleCloseAllDrawers = () => {
      if (isOrgDrawerOpen) {
        handleCloseDrawer();
      }
    };

    window.addEventListener(CLOSE_ALL_DRAWERS_EVENT, handleCloseAllDrawers);
    return () => {
      window.removeEventListener(CLOSE_ALL_DRAWERS_EVENT, handleCloseAllDrawers);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrgDrawerOpen]);

  return (
    <AddDrawerContext.Provider
      value={{
        isOrgDrawerOpen,
        setOrgDrawerOpen: handleSetOrgDrawerOpen,
        setDrawerContent,
        title,
        setTitle,
        tooltip,
        setTooltip,
        closeDrawer: handleCloseDrawer,
        setOnCloseCallback,
        hideDrawerHeader,
        setHideDrawerHeader
      }}
    >
      {/* Render the children passed to this provider */}
      {children}
      <CommonDrawer
        open={isOrgDrawerOpen}
        onClose={handleCloseDrawer}
        drawerId="addDrawer-drawer"
        stackingZIndex={1200}
        PaperProps={{
          className: 'common-drawer-width'
        }}
        sx={drawerSx}
      >
        {/* Drawer Header: Title, Help Link, and Close Button (hidden when hideDrawerHeader is true, e.g. LED Legends view) */}
        {!hideDrawerHeader && (
          <DrawerHeader id="addDrawer-headerBox">
            <Box display="flex" justifyContent="space-between" alignItems="center" id="addDrawer-headerFlex">
              <Box sx={{ display: 'flex', alignItems: 'center' }} id="addDrawer-titleBox">
                <Typography variant="h3" mr={1} id="addDrawer-title">
                  {title}
                </Typography>
                {tooltip}
              </Box>
              <IconButton type="button" onClick={handleCloseDrawer} size="small" id="addDrawer-closeBtn">
                <CloseIcon id="addDrawer-closeIcon" />
              </IconButton>
            </Box>
          </DrawerHeader>
        )}
        {/* Render the component passed as drawer content */}
        {drawerContent}
      </CommonDrawer>
    </AddDrawerContext.Provider>
  );
};
